import type { ActionFunction, HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import type { ClientLoaderFunctionArgs } from '@remix-run/react';
import { useLoaderData, useRouteLoaderData } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import type { RedemptionModalOrderData } from '@shared/dream-components';
import type { TProductReviewsElement } from '@shared/dream-components/src';

import { withLazyBlocks } from '~/components/DreamBuilder/lazyBlock';
import { useMetaViewContent } from '~/components/Pixels/useMetaViewContent';
import type { NodeContent, Page } from '~/interfaces/page';
import type { Product, ProductReviewsResponse } from '~/interfaces/product';
import type { RootLoaderData } from '~/root';
import getProduct from '~/services/api/products/getProduct.server';
import { buildHeaders, buildUrl } from '~/services/api/utils';
import { loaderSession } from '~/session';
import { extractFormResponses } from '~/utils/extractFormResponses';
import { fetchProductReviews } from '~/utils/fetchProductReviews';
import { findTipTapContentNodes } from '~/utils/findTipTapContentNode';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import getMetaDataLinks from '~/utils/getMetaDataLinks';
import { makeAbsoluteUrl } from '~/utils/makeAbsoluteUrl';
import { metaAttributionFromRequest } from '~/utils/metaAttribution.server';
import { publicationFromMatches, siteFromMatches } from '~/utils/publicationFromLoaderData';
import { safeFormData } from '~/utils/safeFormData';
import { BASE_CACHE_TAG } from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import { staticPageLoader } from '../../services/static_page';

import { ClientSecretProvider } from './_components/ClientSecretProvider';
import ProductPageContentWrapper from './_components/ProductContentWrapper';

export interface ProductLoaderData {
  product: Product;
  page: Page;
  stripePublishableKey: string;
  requestUrl: string;
  productReviews?: ProductReviewsResponse;
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': loaderHeaders.get('Cache-Tag') || BASE_CACHE_TAG,
  };
};

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const product = data?.product;
  const publication = publicationFromMatches(matches);
  const requestUrl = data?.requestUrl || '';

  if (!product || !publication) {
    return [{ title: '404 - Not Found' }, { description: 'The newsletter platform built for growth.' }];
  }

  const canonicalUrl = getCanonicalUrl(requestUrl);
  const productUrl = `${publication.url}products/${product.slug}`;

  const productImageUrl = makeAbsoluteUrl(product.default_product_variant?.default_image_url, publication.url);
  const fallbackImageUrl = publication.thumbnail?.url || publication.logo?.url || '';

  return getMergedMetaData({
    canonicalUrl,
    matches,
    pageMetadata: data?.page?.viewable_page_version?.metadata as Record<string, unknown> | undefined,
    siteMetadata: siteFromMatches(matches)?.viewable_site_version?.metadata as Record<string, unknown> | undefined,
    overrides: {
      title: `${product.name} | ${publication.name}`,
      description: product.description || `${product.name} - Available from ${publication.name}`,
      'og:type': 'product',
      'og:url': productUrl,
      'og:title': product.name,
      'og:description': product.description || `${product.name} - Available from ${publication.name}`,
      'og:site_name': publication.name,
      'og:image': productImageUrl || fallbackImageUrl,
      'og:image:alt': product.description || product.name,
      'twitter:url': productUrl,
      'twitter:title': product.name,
      'twitter:description': product.description || `${product.name} - Available from ${publication.name}`,
      'twitter:image': productImageUrl || fallbackImageUrl,
      'twitter:card': 'summary_large_image',
    },
    links: getMetaDataLinks({ renderableFonts: data?.renderableFonts || [] }),
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description || `${product.name} - Available from ${publication.name}`,
      image: productImageUrl,
      url: productUrl,
      brand: {
        '@type': 'Organization',
        name: publication.name,
        url: publication.url,
      },
      offers: {
        '@type': 'Offer',
        price: (product.default_product_variant?.unit_amount ?? 0) / 100,
        priceCurrency: product.default_product_variant?.currency?.toUpperCase() || 'USD',
        availability: product.status === 'live' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: productUrl,
      },
    },
  });
};

export const action: ActionFunction = async ({ request, params }) => {
  const sessionData = await loaderSession(request);
  const { session } = sessionData;
  const formData = await safeFormData(request);
  const productVariantId = formData.get('product_variant_id') as string | null;
  const bookingData = formData.get('booking_data') as string | null;
  const bookingProvider = formData.get('booking_provider') as string | null;
  const webinarAccessToken = formData.get('webinar_access_token') as string | null;
  const bookingAttendeeEmail = formData.get('booking_attendee_email') as string | null;
  const bookingAttendeeName = formData.get('booking_attendee_name') as string | null;

  // Data related to signup so we can attribute the source
  // of signup correctly
  const ref = formData.get('ref');
  const boostAffiliateRecommendingPublicationId = formData.get('bhba');
  const visitToken = formData.get('visit_token');
  const sentFromOrchid = formData.get('sent_from_orchid');
  const purchaseUrl = formData.get('purchase_url') as string | null;
  const anonymousUserId = formData.get('anonymous_user_id') as string | null;
  const nectarVisitToken = formData.get('nectar_visit_token') as string | null;
  const nectarSiteId = formData.get('nectar_site_id') as string | null;
  // Capture bot scores + visitor location data provided from CF's Managed Transform service
  // https://developers.cloudflare.com/rules/transform/managed-transforms/configure/
  // https://developers.cloudflare.com/rules/transform/managed-transforms/reference/
  const cfBotScore = request.headers.get('cf-bot-score');
  const cfVerifiedBot = request.headers.get('cf-verified-bot');
  const cfThreatScore = request.headers.get('cf-threat-score');
  const cfJa3Hash = request.headers.get('cf-ja3-hash');
  const cfJa4 = request.headers.get('cf-ja4');

  const cfIpCity = request.headers.get('cf-ipcity');
  const cfRegion = request.headers.get('cf-region');
  const cfIpCountry = request.headers.get('cf-ipcountry');
  const cfIpLongitude = request.headers.get('cf-iplongitude');
  const cfIpLatitude = request.headers.get('cf-iplatitude');
  const cfTimezone = request.headers.get('cf-timezone');

  if (!productVariantId) {
    return json({ error: 'product_variant_id is required' }, { status: 400 });
  }

  // Fetch product to check if variant is free
  if (!params.slug) {
    return json({ error: 'product_slug is required' }, { status: 400 });
  }

  // Extract form responses (for required form on free product checkout)
  const formResponses = extractFormResponses(formData);

  const endpoint = buildUrl('/products/single_item_checkout_session');
  const endpointHeaders = buildHeaders(request, session);
  const requestBody = {
    nectar_visit_token: nectarVisitToken || undefined,
    product_variant_id: productVariantId,
    booking_data: bookingData ? JSON.parse(bookingData) : null,
    booking_provider: bookingProvider,
    webinar_access_token: webinarAccessToken || null,
    booking_attendee_email: bookingAttendeeEmail,
    booking_attendee_name: bookingAttendeeName,
    form_responses: formResponses.length > 0 ? formResponses : undefined,
    purchase_url: purchaseUrl || undefined,
    anonymous_user_id: anonymousUserId || undefined,
    nectar_site_id: nectarSiteId || undefined,
    ...metaAttributionFromRequest(request),
    signup_data: {
      customer_email: bookingAttendeeEmail,
      visit_token: visitToken,
      ref,
      sent_from_orchid: sentFromOrchid,
      recommending_publication_id: boostAffiliateRecommendingPublicationId,
      cf_bot_score: cfBotScore,
      cf_verified_bot: cfVerifiedBot,
      cf_threat_score: cfThreatScore,
      cf_ja3_hash: cfJa3Hash,
      cf_ja4: cfJa4,
      cf_ip_city: cfIpCity,
      cf_region: cfRegion,
      cf_ip_country: cfIpCountry,
      cf_ip_longitude: cfIpLongitude,
      cf_ip_latitude: cfIpLatitude,
      cf_timezone: cfTimezone,
    },
  };

  return await fetch(endpoint, {
    method: 'POST',
    headers: endpointHeaders,
    body: JSON.stringify(requestBody),
  })
    .then(async (res) => {
      const response = await res.json();

      if (!res.ok) {
        return json({
          toast: {
            status: 'error',
            message: response.message,
          },
        });
      }

      // Free products return order object (and redemption_token for instant redemption)
      if (response.order) {
        return json({
          toast: {
            status: 'success',
            message: 'Order created successfully',
          },
          order: response.order as RedemptionModalOrderData,
          redemptionToken: response.redemption_token,
        });
      }

      // Paid products return client_secret and redemption_token
      return json({
        toast: {
          status: 'success',
          message: 'Checkout session created successfully',
        },
        clientSecret: response.client_secret,
        redemptionToken: response.redemption_token,
      });
    })
    .catch((error) => json({ toast: { status: 'error', message: error.message } }));
};

export const loader: LoaderFunction = async ({ request, params, context }) => {
  const { session } = await loaderSession(request);

  if (!params.slug) {
    return redirect('/');
  }

  try {
    const product = await getProduct(request, session, params.slug);

    return staticPageLoader({
      context,
      additionalData: {
        product,
        stripePublishableKey: process.env.STRIPE_PUBLISHABLE_KEY,
        requestUrl: request.url,
      },
      request,
      pageRequestParams: {
        pathname: 'products/$slug',
      },
    });
  } catch {
    throw new Response('Product not found', { status: 404 });
  }
};

export const clientLoader = withLazyBlocks(async ({ serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<ProductLoaderData>();
  if (!serverData?.page) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  const content = structuredClone(serverData?.page?.viewable_page_version?.content);
  if (!content) {
    return serverData;
  }

  const productReviewsNodes = findTipTapContentNodes(
    content as NodeContent,
    'productReviews',
  ) as unknown as TProductReviewsElement[];

  if (productReviewsNodes.length === 0) {
    return serverData;
  }

  try {
    // Use the last block that shows reviews list, or fall back to the last block
    const nodesWithReviewsList = productReviewsNodes.filter((node) => !node.attrs?.hideReviewsList);
    const targetNode = nodesWithReviewsList.at(-1) ?? productReviewsNodes.at(-1);
    const reviewsPerPage = targetNode?.attrs?.reviewsPerPage || 4;

    const productReviewsRes = await fetchProductReviews(serverData.product.slug, 1, reviewsPerPage);
    if (productReviewsRes === null) {
      return serverData;
    }

    return {
      ...serverData,
      productReviews: productReviewsRes as ProductReviewsResponse,
    };
  } catch (error) {
    Sentry.captureException(error);
  }

  return serverData;
});

clientLoader.hydrate = true;

export default function ProductShow() {
  const { stripePublishableKey, page, product } = useLoaderData<typeof loader>() as ProductLoaderData;
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const variant = product?.default_product_variant;
  useMetaViewContent(
    product?.id,
    product
      ? {
          content_type: 'product',
          content_ids: [product.id],
          content_name: product.name,
          ...(variant ? { value: variant.unit_amount / 100, currency: variant.currency.toUpperCase() } : {}),
        }
      : null,
  );

  return (
    <ClientSecretProvider>
      <ProductPageContentWrapper stripePublishableKey={stripePublishableKey} page={page} site={site} />
    </ClientSecretProvider>
  );
}

export const shouldRevalidate = shouldRevalidateLoaderData;
